import { useState } from 'react';
import { Phone, Plus, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useEscalationContacts, useUpsertEscalationContact } from '@/api/hooks/useSettings';
import { RequireRole } from '@/components/auth/RequireRole';
import { PrimaryAdminReferenceCard } from '@/components/settings/PrimaryAdminReferenceCard';
import type { EscalationContact } from '@/api/types/settings.types';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
];

type EscalationContactType = 'primary' | 'secondary' | 'billing';

const CONTACT_TYPE_LABEL: Record<EscalationContactType, string> = {
  primary: 'Primary Business Contact',
  secondary: 'Secondary Business Contact',
  billing: 'Billing Contact',
};

function ContactModal({
  contact,
  contactType,
  onClose,
}: {
  contact?: EscalationContact;
  contactType: EscalationContactType;
  onClose: () => void;
}) {
  const upsertMutation = useUpsertEscalationContact();
  const [form, setForm] = useState({
    id: contact?.id,
    contact_type: contactType,
    label: contact?.label ?? '',
    email: contact?.distribution_email ?? '',
    phone: contact?.phone ?? '',
    preferred_method: contact?.preferred_method ?? 'email',
    available_hours: contact?.available_hours ?? '24x7',
    timezone: contact?.timezone ?? 'UTC',
    notes: contact?.notes ?? '',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // S4.1: map the snake_case form to the camelCase body the backend zod expects
    // (mirrors the admin TenantContactsTab mutationFn → consistent write contract).
    upsertMutation.mutate({
      id: form.id,
      contactType: form.contact_type,
      label: form.label,
      distributionEmail: form.email,
      phone: form.phone || undefined,
      preferredMethod: form.preferred_method,
      availableHours: form.available_hours,
      timezone: form.timezone,
      notes: form.notes || undefined,
    }, {
      onSuccess: () => { toast.success('Contact saved'); onClose(); },
      onError: () => toast.error('Failed to save contact'),
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{contact ? 'Edit' : 'Add'} {CONTACT_TYPE_LABEL[contactType]}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Label <span className="text-accent-rose">*</span></Label>
            <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. IT Security Team" required />
          </div>
          <div className="space-y-1.5">
            <Label>Email <span className="text-accent-rose">*</span></Label>
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+1 555 000 0000" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Preferred method</Label>
              <select value={form.preferred_method} onChange={(e) => setForm((f) => ({ ...f, preferred_method: e.target.value as typeof form.preferred_method }))}
                className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none">
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Available hours</Label>
              <select value={form.available_hours} onChange={(e) => setForm((f) => ({ ...f, available_hours: e.target.value as typeof form.available_hours }))}
                className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none">
                <option value="24x7">24×7</option>
                <option value="business_hours">Business hours</option>
                <option value="on_call">On-call</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Timezone</Label>
            <select value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
              className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary focus:outline-none">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none" />
          </div>
          <div className="flex justify-between pt-2 border-t border-border-subtle">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={upsertMutation.isPending || !form.label || !form.email}>Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ contact, contactType }: { contact?: EscalationContact; contactType: EscalationContactType }) {
  const [editing, setEditing] = useState(false);
  const title = CONTACT_TYPE_LABEL[contactType];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Phone className="h-4 w-4 text-text-secondary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {contact ? (
          <div className="space-y-2">
            <p className="font-medium text-text-primary">{contact.label}</p>
            <div className="space-y-1 text-sm text-text-secondary">
              <p>{contact.distribution_email}</p>
              {contact.phone && <p>{contact.phone}</p>}
              <p className="capitalize">{contact.preferred_method} · {contact.available_hours.replace('_', ' ')} · {contact.timezone}</p>
              {contact.notes && <p className="text-xs italic">{contact.notes}</p>}
            </div>
            {/* CONTACTS-S4: dual-route last-edited (side + date; null pre-S3 → nothing). */}
            {contact.updated_by_side && (
              <p className="text-xs text-text-secondary" data-testid={`last-edited-${contactType}`}>
                Last edited by {contact.updated_by_side === 'admin' ? 'an admin' : 'your team'} on{' '}
                {new Date(contact.updated_at).toLocaleDateString()}
              </p>
            )}
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setEditing(true)}>
              <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-sm text-text-secondary">No contact configured</p>
            <Button size="sm" onClick={() => setEditing(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Contact
            </Button>
          </div>
        )}
      </CardContent>
      {editing && (
        <ContactModal contact={contact} contactType={contactType} onClose={() => setEditing(false)} />
      )}
    </Card>
  );
}

export function EscalationContactsPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Escalation Contacts">
      <EscalationContactsPageBody />
    </RequireRole>
  );
}

function EscalationContactsPageBody() {
  const { data: contacts, isLoading } = useEscalationContacts();

  const primary = contacts?.find((c) => c.contact_type === 'primary');
  const secondary = contacts?.find((c) => c.contact_type === 'secondary');
  // Billing is now tenant-editable (MI decision) — the SAME shared tenant_contacts billing row
  // the platform admin also manages (one active per tenant_id+contact_type).
  const billing = contacts?.find((c) => c.contact_type === 'billing');

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Escalation Contacts</h1>
        <p className="mt-1 text-sm text-text-secondary">Who to contact for critical alerts</p>
      </div>

      {isLoading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <ContactCard contact={primary} contactType="primary" />
          <ContactCard contact={secondary} contactType="secondary" />
          <ContactCard contact={billing} contactType="billing" />
          {/* Read-only reference (reused from the Team page) — identity, not an escalation
              contact; changing it goes through Support. Clear of B-PRIMARY-USER-ROLE-SEMANTICS-1. */}
          <PrimaryAdminReferenceCard />
        </div>
      )}
    </div>
  );
}
